import { Popup } from "../../../SharedComponents/Popup";
import { SvgIcons } from "../../../SharedComponents/SvgIcons/SvgIcons";
import Dropdown from "../../../../UI/Dropdown/Dropdown";
import { ErrorText, WarningText, SuccessText } from "../../ui/StatusText";
import { FileUploadField } from "../../ui/FileUploadField";
import { usePanDetailsForm } from "../../services/hooks/usePanDetailsForm";
import { getUserId } from "../../../../utils/auth";
import {
  NO_PAN_REASONS,
  OTHER_NO_PAN_REASON,
  PAN_STATUS_MESSAGES,
} from "../../services/utils/constants";
import styles from "./style.module.css";

export const EditPanDetailsModal = ({
  isOpen,
  onClose,
  currentDetails = {},
  onSaveSuccess,
}) => {
  const userId = getUserId();
  const pan = usePanDetailsForm({ userId, currentDetails, isOpen });
  const { state, loading, derived, handlers, mutators } = pan;

  if (!isOpen) return null;

  return (
    <Popup onClose={onClose} contentClassName={styles.modalContent} hideCloseButton>
      <div className={styles.stickyHeader}>
        <span className={styles.title}>PAN details</span>
        <button type="button" className={styles.closeBtn} onClick={onClose} aria-label="Close">
          <SvgIcons name="cross_btn" width="20" height="20" style={{ color: "#999" }} />
        </button>
      </div>
      <div className={styles.formBody}>
        <div className={styles.fieldGroup}>
          <label className={styles.label}>
            Do you have a PAN? <span>*</span>
          </label>
          <div className={styles.toggleRow}>
            <button
              type="button"
              disabled={loading.isSaving}
              className={`${styles.toggleBtn} ${state.hasPan === "yes" ? styles.toggleBtnActive : ""}`}
              onClick={() => handlers.setHasPan("yes")}
            >
              Yes, I have a PAN
            </button>
            <button
              type="button"
              disabled={loading.isSaving}
              className={`${styles.toggleBtn} ${state.hasPan === "no" ? styles.toggleBtnActive : ""}`}
              onClick={() => handlers.setHasPan("no")}
            >
              No
            </button>
          </div>
          {state.errors.hasPan && <ErrorText>{state.errors.hasPan}</ErrorText>}
          {state.hasPan === "no" && (
            <div className={styles.hintRow}>
              <SvgIcons name="info_circle" width="14" height="14" className={styles.hintIcon} />
              <span className={styles.hintText}>
                As per regulations, 20% TDS applies if PAN is not provided
              </span>
            </div>
          )}
        </div>

        <div className={styles.divider} />

        {state.hasPan === "yes" ? (
          <>
            <div className={styles.fieldGroup}>
              <label className={styles.label}>Is PAN linked with Aadhar?</label>
              <div className={styles.toggleRow}>
                <button
                  type="button"
                  disabled={loading.isSaving}
                  className={`${styles.toggleBtn} ${state.isAadharLinked === "yes" ? styles.toggleBtnActive : ""}`}
                  onClick={() => handlers.setIsAadharLinked("yes")}
                >
                  Yes, it is linked
                </button>
                <button
                  type="button"
                  disabled={loading.isSaving}
                  className={`${styles.toggleBtn} ${state.isAadharLinked === "no" ? styles.toggleBtnActive : ""}`}
                  onClick={() => handlers.setIsAadharLinked("no")}
                >
                  No
                </button>
              </div>
              {state.isAadharLinked === "no" && (
                <div className={styles.hintRow}>
                  <SvgIcons name="info_circle" width="14" height="14" className={styles.hintIcon} />
                  <span className={styles.hintText}>
                    As per regulations, 20% TDS applies if PAN is not linked with Aadhaar.
                  </span>
                </div>
              )}
            </div>

            <div className={styles.fieldGroup}>
              <label className={styles.label}>
                Full name as per PAN <span>*</span>
              </label>
              <input
                disabled={loading.isSaving}
                className={`${styles.input} ${state.errors.fullName ? styles.inputError : ""}`}
                value={state.fullName}
                onChange={(e) => handlers.setFullName(e.target.value)}
                placeholder="Enter your name as per PAN"
              />
              {state.errors.fullName && <ErrorText>{state.errors.fullName}</ErrorText>}
            </div>

            <div className={styles.fieldGroup}>
              <label className={styles.label}>
                PAN Number <span>*</span>
              </label>
              <input
                disabled={loading.isSaving}
                className={`${styles.input} ${state.errors.panNumber ? styles.inputError : ""}`}
                value={state.panNumber}
                onChange={(e) => handlers.setPanNumber(e.target.value)}
                placeholder="ABCDE9876F"
                maxLength={10}
              />
              {state.errors.panNumber && <ErrorText>{state.errors.panNumber}</ErrorText>}
              {loading.isSaving && <span className={styles.verifyingText}>Verifying your PAN details...</span>}
              {!loading.isSaving && state.verificationStatus === "verified" && (
                <SuccessText>PAN verified successfully</SuccessText>
              )}
              {!loading.isSaving && state.verificationStatus === "failed" && (
                <ErrorText>{PAN_STATUS_MESSAGES[state.verificationStatus]}</ErrorText>
              )}
              {!loading.isSaving &&
                PAN_STATUS_MESSAGES[state.verificationStatus] &&
                state.verificationStatus !== "failed" && (
                  <WarningText>{PAN_STATUS_MESSAGES[state.verificationStatus]}</WarningText>
                )}
            </div>

            <div className={styles.fieldGroup}>
              <label className={styles.label}>
                Date of Birth (as per PAN) <span>*</span>
              </label>
              <input
                type="date"
                required
                disabled={loading.isSaving}
                className={`${styles.input} ${state.errors.dob ? styles.inputError : ""}`}
                value={state.dob}
                onChange={(e) => handlers.setDob(e.target.value)}
                max={new Date().toISOString().split("T")[0]}
              />
              {state.errors.dob && <ErrorText>{state.errors.dob}</ErrorText>}
            </div>

            <div className={styles.fieldGroup}>
              <label className={styles.label}>
                Upload PAN <span>*</span>
              </label>
              <FileUploadField
                inputRef={state.fileInputRef}
                file={state.panFile}
                existingUrl={state.existingPanDocumentUrl}
                existingName={state.existingPanDocumentName}
                onView={() => handlers.viewDocument("pan_copy")}
                onSelect={handlers.handleFileSelect}
                accept=".jpg,.jpeg,.png,.pdf"
                hint="JPG, PNG or PDF"
                error={state.errors.panFile}
                disabled={loading.isSaving}
              />
              {state.errors.panFile && <ErrorText>{state.errors.panFile}</ErrorText>}
              {state.apiError && (
                <div className={styles.apiErrorRow}>
                  <SvgIcons name="error_circle_icon" width="16" height="16" className={styles.apiErrorIcon} />
                  <span className={styles.apiErrorText}>{state.apiError}</span>
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            <div className={styles.fieldGroup}>
              <label className={styles.label}>
                Reason for not having PAN <span>*</span>
              </label>
              <button
                ref={state.reasonTriggerRef}
                type="button"
                disabled={loading.isSaving}
                className={`${styles.selectTrigger} ${state.errors.reasonForNoPan ? styles.inputError : ""}`}
                onClick={handlers.toggleReasonDropdown}
              >
                <span
                  className={`${styles.selectTriggerLabel} ${state.reasonForNoPan ? styles.selectTriggerLabelFilled : ""
                    }`}
                >
                  {NO_PAN_REASONS.find((reason) => reason.value === state.reasonForNoPan)?.label ||
                    "Select reason"}
                </span>
                <SvgIcons
                  name="chevron_down"
                  width={20}
                  height={20}
                  style={{
                    color: "#858585",
                    flexShrink: 0,
                    transform: state.isReasonDropdownOpen ? "rotate(180deg)" : undefined,
                    transition: "transform 0.15s",
                  }}
                />
              </button>
              <Dropdown
                anchorRef={state.reasonTriggerRef}
                open={state.isReasonDropdownOpen}
                onClose={handlers.closeReasonDropdown}
                className={styles.selectDropdownPanel}
              >
                <div className={styles.selectList}>
                  {NO_PAN_REASONS.map((reason) => (
                    <button
                      key={reason.value}
                      type="button"
                      className={`${styles.selectRow} ${reason.value === state.reasonForNoPan ? styles.selectRowSelected : ""
                        }`}
                      onClick={() => handlers.setReasonForNoPan(reason.value)}
                    >
                      {reason.label}
                    </button>
                  ))}
                </div>
              </Dropdown>
              {state.errors.reasonForNoPan && <ErrorText>{state.errors.reasonForNoPan}</ErrorText>}
              {state.reasonForNoPan === OTHER_NO_PAN_REASON && (
                <>
                  <input
                    disabled={loading.isSaving}
                    className={`${styles.input} ${state.errors.otherReasonText ? styles.inputError : ""}`}
                    value={state.otherReasonText}
                    onChange={(e) => handlers.setOtherReasonText(e.target.value)}
                    placeholder="Please specify your reason"
                  />
                  {state.errors.otherReasonText && <ErrorText>{state.errors.otherReasonText}</ErrorText>}
                </>
              )}
            </div>

            <div className={styles.fieldGroup}>
              <label className={styles.label}>
                Upload Form 60 <span>*</span>
              </label>
              <FileUploadField
                inputRef={state.form60InputRef}
                file={state.form60File}
                existingUrl={state.existingForm60DocumentUrl}
                existingName={state.existingForm60DocumentName}
                onView={() => handlers.viewDocument("form_60")}
                onSelect={handlers.handleForm60Select}
                accept=".jpg,.jpeg,.png,.pdf"
                hint="Signed document · JPG, PNG or PDF"
                error={state.errors.form60File}
                disabled={loading.isSaving}
              />
              {state.errors.form60File && <ErrorText>{state.errors.form60File}</ErrorText>}
              {state.apiError && (
                <div className={styles.apiErrorRow}>
                  <SvgIcons name="error_circle_icon" width="16" height="16" className={styles.apiErrorIcon} />
                  <span className={styles.apiErrorText}>{state.apiError}</span>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <div className={styles.stickyFooter}>
        <button
          className={styles.saveBtn}
          onClick={() => mutators.handleSave({ onSaveSuccess, onClose })}
          disabled={loading.isSaving || !derived.isFormComplete}
        >
          {loading.isSaving && <span className={styles.spinner} />}
          Save details
        </button>
      </div>
    </Popup>
  );
};
